import { OpenFeature, InMemoryProvider } from "@openfeature/web-sdk";

// window.FF - the jet feature-flag facade over OpenFeature.
//
// The provider is OpenFeature's InMemoryProvider seeded from ENV.featureFlags
// (js/env.js), so flags are static per build and evaluate synchronously with no
// network round-trip. A flag value is set by editing that object; see the note
// there about per-lane values. Any OpenFeature-compatible provider
// (LaunchDarkly, Flagsmith, flagd, ...) can be swapped in at buildProvider()
// without touching the FF.* API or its callers.

const state = {
	ready: false,
	initPromise: null,
	client: null,
};

function readEnv() {
	let env = {};
	try {
		if (typeof ENV !== "undefined") env = ENV;
		else if (typeof window !== "undefined" && window.ENV) env = window.ENV;
	} catch (e) {
		env = (typeof window !== "undefined" && window.ENV) || {};
	}
	return {
		flags: applyLocalOverrides(env.featureFlags && typeof env.featureFlags === "object" ? env.featureFlags : {}),
		version: env.version || "0.0.0",
	};
}

// Local overrides. localStorage["ff:<flag>"] wins over the seeded value for
// that flag, on every lane, in that one browser: "true" / "false" become
// booleans, any other value is parsed as JSON and falls back to the raw
// string. This is how QA and developers flip a flag without a deploy. Flags
// gate client-side UI only; the server still enforces permissions, so an
// override has no security effect. Blocked storage leaves the seeded values.
const OVERRIDE_PREFIX = "ff:";

function applyLocalOverrides(flags) {
	let storage = null;
	try {
		storage = typeof window !== "undefined" ? window.localStorage : null;
	} catch (e) {
		storage = null;
	}
	if (!storage) return flags;
	const merged = Object.assign({}, flags);
	try {
		for (let i = 0; i < storage.length; i++) {
			const name = storage.key(i);
			if (!name || name.indexOf(OVERRIDE_PREFIX) !== 0) continue;
			const raw = storage.getItem(name);
			if (raw === null) continue;
			merged[name.slice(OVERRIDE_PREFIX.length)] = parseOverride(raw);
		}
	} catch (e) {
		return flags;
	}
	return merged;
}

function parseOverride(raw) {
	const trimmed = String(raw).trim();
	if (trimmed === "true") return true;
	if (trimmed === "false") return false;
	try {
		return JSON.parse(trimmed);
	} catch (e) {
		return trimmed;
	}
}

// ENV.featureFlags values are plain defaults; each becomes a single-variant
// flag so getBooleanValue / getStringValue / ... resolve to that value.
function toFlagConfiguration(flags) {
	const cfg = {};
	for (const key of Object.keys(flags)) {
		const value = flags[key];
		if (value === undefined || value === null) continue;
		cfg[key] = {
			disabled: false,
			variants: { default: value },
			defaultVariant: "default",
		};
	}
	return cfg;
}

function buildProvider() {
	return new InMemoryProvider(toFlagConfiguration(readEnv().flags));
}

async function init(ctx) {
	if (state.initPromise) return state.initPromise;
	state.initPromise = (async () => {
		try {
			const provider = buildProvider();
			await OpenFeature.setProviderAndWait(provider);
			await OpenFeature.setContext({
				targetingKey: String(ctx && ctx.accountId != null ? ctx.accountId : ""),
				account_id: ctx && ctx.accountId != null ? ctx.accountId : null,
				user_id: ctx && ctx.userId != null ? ctx.userId : null,
				email: ctx && ctx.email ? ctx.email : null,
			});
			state.client = OpenFeature.getClient();
			state.ready = true;
		} catch (e) {
			console.warn("[FF] init failed; flags will return defaults", e);
			state.ready = false;
			state.client = null;
			state.initPromise = null;
		}
	})();
	return state.initPromise;
}

function isEnabled(key, defaultValue) {
	const fallback = defaultValue === true;
	if (!state.ready || !state.client) return fallback;
	try {
		return state.client.getBooleanValue(key, fallback);
	} catch (e) {
		return fallback;
	}
}

function getString(key, defaultValue) {
	const fallback = defaultValue == null ? "" : String(defaultValue);
	if (!state.ready || !state.client) return fallback;
	try {
		return state.client.getStringValue(key, fallback);
	} catch (e) {
		return fallback;
	}
}

function getNumber(key, defaultValue) {
	const fallback = typeof defaultValue === "number" ? defaultValue : 0;
	if (!state.ready || !state.client) return fallback;
	try {
		return state.client.getNumberValue(key, fallback);
	} catch (e) {
		return fallback;
	}
}

function getJSON(key, defaultValue) {
	const fallback = defaultValue == null ? null : defaultValue;
	if (!state.ready || !state.client) return fallback;
	try {
		return state.client.getObjectValue(key, fallback);
	} catch (e) {
		return fallback;
	}
}

function isReady() {
	return state.ready;
}

window.FF = { init, isEnabled, getString, getNumber, getJSON, isReady };
